/** @prettier */

/**
 *
 */

import {Card, CardContent, Grid, Box, Skeleton, Stack} from '@mui/material';
import {useEffect, useState, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';
//MTH component import
import GearMenu, {GearMenuListArgType} from '../GearMenuList';
import ImageUploadDialog from './ImageUploadDialog';
import LayoutSettingDialog from './LayoutSettingDialog';
import ImageDeleteDialog from './ImageDeleteDialog';
//API import
import {getCompositionsLayoutImage} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
//type import
import {layoutUnitListType} from '../../Type/TypeDef';

import {iconBlack} from '../../define';

type IconListType = {
  iconTop: number;
  iconLeft: number;
  unitID: string;
  unitName: string;
}[];

/**
 * グループレイアウト
 * @param props props
 * @returns グループレイアウト
 */
const GroupLayoutSetting = (props: {
  /** 表示しているグループの情報 */
  displayGroupInfo: apiIF.res.getCompositionsGroupsByGroupID;
  /** 表示しているグループの情報（set関数） */
  setDisplayGroupInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsGroupsByGroupID | null>
  >;
  /** サイトにあるSL、SLIFユニットのリスト */
  unitList?: layoutUnitListType;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const layoutElement = useRef<HTMLDivElement>(null); //アイコン表示用要素
  const imageElement = useRef<HTMLImageElement>(null); //画像表示用要素
  //パスパラメータ取得
  const {siteID, groupID} = useParams();

  const [layoutImage, setLayoutImage] = useState<string | null>(null); //レイアウト画像
  const [iconList, setIconList] = useState<IconListType>([]); //アイコンの座標一覧
  const [isOpenUploadDialog, setIsOpenUploadDialog] = useState(false); //アップロードダイアログ表示フラグ
  const [isOpenLayoutDialog, setIsOpenLayoutDialog] = useState(false); //レイアウト設定ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ

  //レイアウト画像を取得
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || groupID === undefined) {
      return;
    }

    //レイアウト画像を初期化
    setLayoutImage(null);

    getCompositionsLayoutImage(siteID, groupID).then(response => {
      setLayoutImage(response['layout-image']);
    });
  }, [siteID, groupID]);

  //レイアウト画像を表示
  useEffect(() => {
    //画像・要素を取得できない場合
    if (imageElement.current === null || layoutImage === null) return;

    imageElement.current.src = layoutImage;
  }, [imageElement, layoutImage]);

  //アイコンの座標を取得
  useEffect(() => {
    //要素を取得できない場合
    if (layoutElement.current === null) return;

    //アイコンの座標を初期化
    setIconList([]);

    setIconList(
      props.displayGroupInfo['unit-informations'].reduce((result, info) => {
        const xCoordinate = Number(info['x-coordinate']);
        const yCoordinate = Number(info['y-coordinate']);
        //初期位置のアイコンはスキップ
        if (xCoordinate < 0 || yCoordinate < 0) {
          return result;
        }

        result.push({
          unitID: info['unit-id'],
          unitName: info['unit-name'],
          iconLeft: xCoordinate,
          iconTop: yCoordinate,
        });
        return result;
      }, [] as IconListType)
    );
  }, [props.displayGroupInfo, layoutImage, layoutElement]);

  /**
   * Upload imageボタン押下時処理
   */
  const openUploadDialog = () => {
    setIsOpenUploadDialog(true);
  };
  /**
   * Layout settingボタン押下時処理
   */
  const openLayoutSettingDialog = () => {
    setIsOpenLayoutDialog(true);
  };
  /**
   * Delete imageボタン押下時処理
   */
  const openDeleteDialog = () => {
    setIsOpenDeleteDialog(true);
  };

  /**
   * 歯車メニューリスト
   * @returns メニュー一覧
   */
  const gearMenuItem = (trans: TFunction): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: 'mv-uploadImage',
        label: trans('GroupLayoutSetting.gearMenu.uploadImage'),
        onClick: () => openUploadDialog(),
        icon: {
          className: 'groupSetting__contents__button__icon',
          src: iconBlack.UPLOAD.src,
          alt: trans(
            `GroupLayoutSetting.image.alternativeText.${iconBlack.UPLOAD.alt}`
          ),
        },
      },
      {
        id: 'mv-latoutSetting',
        label: trans('GroupLayoutSetting.gearMenu.layoutSetting'),
        onClick: () => openLayoutSettingDialog(),
        icon: {
          className: 'groupSetting__contents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `GroupLayoutSetting.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      },
      {
        id: 'mv-deleteImage',
        label: trans('GroupLayoutSetting.gearMenu.deleteImage'),
        onClick: () => openDeleteDialog(),
        icon: {
          className: 'groupSetting__contents__button__icon',
          src: iconBlack.CLOSE.src,
          alt: trans('GroupLayoutSetting.image.alternativeText.delete'),
        },
      },
    ];
  };

  /**
   * ユニットアイコン
   * @param props props
   * @returns ユニットアイコン
   */
  const UnitIcon = (props: {unitID: string; unitList?: layoutUnitListType}) => {
    let unitType: layoutUnitListType[0]['unitType'] | '';
    if (props.unitList !== undefined) {
      const result = props.unitList.find(info => info.unitID === props.unitID);
      unitType = result === undefined ? '' : result.unitType;
    }
    /** 型式毎のアイコン */
    let iconSrc = '';
    /** 型式毎のalt属性用の翻訳キー */
    let transKeyForAlt = '';
    switch (unitType) {
      case 'AC':
        iconSrc = iconBlack.AC_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.AC_ON.alt}`;
        break;
      case 'FDT':
        iconSrc = iconBlack.FDT_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.FDT_ON.alt}`;
        break;
      case 'FDTS':
        iconSrc = iconBlack.FDTS_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.FDTS_ON.alt}`;
        break;
      case 'FDTW':
        iconSrc = iconBlack.FDTW_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.FDTW_ON.alt}`;
        break;
      case 'FDE':
        iconSrc = iconBlack.FDE_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.FDE_ON.alt}`;
        break;
      case 'FDF':
        iconSrc = iconBlack.FDF_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.FDF_ON.alt}`;
        break;
      case 'FDFW':
        iconSrc = iconBlack.FDFW_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.FDFW_ON.alt}`;
        break;
      case 'FDK':
        iconSrc = iconBlack.FDK_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.FDK_ON.alt}`;
        break;
      case 'FDU':
        iconSrc = iconBlack.FDU_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.FDU_ON.alt}`;
        break;
      case 'VENT':
        iconSrc = iconBlack.VENTILATOR_UNIT_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.VENTILATOR_UNIT_ON.alt}`;
        break;
      case 'LIGHT':
        iconSrc = iconBlack.LIGHT_UNIT_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.LIGHT_UNIT_ON.alt}`;
        break;
      case 'Hyozan':
        iconSrc = iconBlack.FREEZER_ON.src;
        transKeyForAlt = `GroupLayoutSetting.image.alternativeText.${iconBlack.FREEZER_ON.alt}`;
        return (
          <Box className="groupSetting__contents__layoutContents__layoutBox--view__iconBox__icon__hyozanBorder">
            <img
              className="groupSetting__contents__layoutContents__layoutBox--view__iconBox__icon__hyozanBorder__image"
              src={iconSrc}
              alt={`${trans(transKeyForAlt)}`}
            />
          </Box>
        );
    }
    return (
      <Box className="groupSetting__contents__layoutContents__layoutBox--view__iconBox__icon__hyozanBorder">
        <img
          className="groupSetting__contents__layoutContents__layoutBox--view__iconBox__icon__border__image"
          src={iconSrc}
          alt={`${trans(transKeyForAlt)}`}
        />
      </Box>
    );
  };

  return (
    <Card className="groupSetting__contents--layout">
      <CardContent>
        <Stack spacing={2}>
          {/* 歯車ボタン */}
          <Grid
            container
            justifyContent="space-between"
            className="groupSetting__contents__title">
            <Grid item xs={2}>
              {trans('GroupLayoutSetting.title')}
            </Grid>
            <Grid item xs={1} className="groupSetting__contents__layoutGear">
              <GearMenu
                id="groupLayoutGearBtn"
                menuItem={gearMenuItem(trans)}
              />
              <ImageUploadDialog
                setLayoutImage={setLayoutImage}
                isOpenDialog={isOpenUploadDialog}
                setIsOpenDialog={setIsOpenUploadDialog}
              />
              <LayoutSettingDialog
                groupInfo={props.displayGroupInfo}
                setGroupInfo={props.setDisplayGroupInfo}
                layoutImage={layoutImage}
                isOpenDialog={isOpenLayoutDialog}
                setIsOpenDialog={setIsOpenLayoutDialog}
                unitList={props.unitList}
              />
              <ImageDeleteDialog
                setLayoutImage={setLayoutImage}
                isOpenDialog={isOpenDeleteDialog}
                setIsOpenDialog={setIsOpenDeleteDialog}
              />
            </Grid>
          </Grid>
          {/* レイアウト画像 */}
          <Box className="groupSetting__contents__layoutContents">
            {layoutImage === null ? (
              <Skeleton variant="rectangular">
                <Box className="groupSetting__contents__layoutContents__layoutBox--view" />
              </Skeleton>
            ) : (
              <Box
                className="groupSetting__contents__layoutContents__layoutBox--view"
                ref={layoutElement}>
                <img
                  className="groupSetting__contents__layoutContents__image"
                  ref={imageElement}
                  alt={`${trans(
                    'GroupLayoutSetting.image.alternativeText.groupLayoutImage'
                  )}`}
                />
                {/* 座標と画像の読み込み完了後にユニットアイコンを表示する */}
                {iconList.map(info => {
                  return (
                    <Box
                      key={info.unitID}
                      className="groupSetting__contents__layoutContents__layoutBox--view__iconBox"
                      style={{
                        top: `${info.iconTop}%`,
                        left: `${info.iconLeft}%`,
                      }}>
                      <Grid container>
                        {/* ユニットアイコン */}
                        <Grid item xs="auto">
                          <UnitIcon
                            unitID={info['unitID']}
                            unitList={props.unitList}
                          />
                        </Grid>
                        {/* ユニット名称 */}
                        <Grid item xs={12}>
                          <Box
                            title={info.unitName}
                            className="groupSetting__contents__layoutContents__layoutBox--view__iconBox__unitNameBox">
                            <Box
                              component="span"
                              className="groupSetting__contents__layoutContents__layoutBox--view__iconBox__unitNameBox__text">
                              {info.unitName}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
export default GroupLayoutSetting;
